import { Link } from "react-router-dom";
import { Check, Minus, ArrowRight, ShieldCheck, GitBranch, Users } from "lucide-react";
import { NarrativeCard, Tooltip } from "@/design/components";

// Capability matrix (v3 §Chunk 7). Mark: full, scoped, or none.
type Cap = "y" | "scoped" | "n";

const CAPS = [
  "Raise spend",
  "Approve spend",
  "Record / reconcile",
  "Route disbursement",
  "Approve disbursement",
  "Independently verify",
  "Set team thresholds",
  "See full programme budget",
] as const;

const ROLES: { role: string; org: string; caps: Cap[] }[] = [
  { role: "Programme Director", org: "Stratus (oversight)", caps: ["n", "scoped", "n", "y", "n", "y", "n", "y"] },
  { role: "Monitoring & verification", org: "Stratus (oversight)", caps: ["n", "n", "n", "n", "n", "y", "n", "y"] },
  { role: "Programme Officer", org: "Danish Impact Fund", caps: ["n", "n", "n", "n", "y", "n", "n", "y"] },
  { role: "Investment committee", org: "Danish Impact Fund", caps: ["n", "n", "n", "n", "y", "n", "n", "y"] },
  { role: "Prime contractor lead", org: "Contractor", caps: ["y", "y", "y", "n", "n", "n", "y", "scoped"] },
  { role: "Sub-contractor PM", org: "Sub-contractor", caps: ["y", "scoped", "y", "n", "n", "n", "y", "n"] },
  { role: "Field reporter", org: "Contractor", caps: ["y", "n", "scoped", "n", "n", "n", "n", "n"] },
];

function Cell({ c }: { c: Cap }) {
  if (c === "y") return <Check className="mx-auto h-4 w-4 text-ok" aria-label="Full" />;
  if (c === "scoped")
    return (
      <Tooltip label="Scoped to their part">
        <span className="mx-auto block h-2 w-2 rounded-full bg-warnFill" aria-label="Scoped to their part" />
      </Tooltip>
    );
  return <Minus className="mx-auto h-3.5 w-3.5 text-faint" aria-label="Not permitted" />;
}

export function AccessView() {
  return (
    <div className="space-y-6">
      <NarrativeCard
        variant="briefing"
        title="Who can do what, and the controls that hold it together"
        body="Access is scoped per organisation and per role. Three controls keep the chain accountable: delegated authority (each level sets the spend threshold for the level below), segregation of duties (the person who raises a spend cannot approve it), and multi-signature (large milestone disbursements need two funder sign-offs)."
      />

      <section className="overflow-x-auto rounded-card border border-line bg-card">
        <table className="w-full min-w-[44rem] text-sm">
          <thead>
            <tr className="border-b border-line bg-subtle/60 text-left text-meta text-faint">
              <th className="sticky left-0 z-10 bg-subtle/60 px-4">Role</th>
              {CAPS.map((c) => <th key={c} className="px-2 text-center">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {ROLES.map((r) => (
              <tr key={r.role} className="h-11 border-b border-hairline last:border-0">
                <td className="sticky left-0 z-10 bg-card px-4">
                  <p className="font-medium text-ink">{r.role}</p>
                  <p className="text-xs text-faint">{r.org}</p>
                </td>
                {r.caps.map((c, i) => <td key={i} className="px-2 text-center">{<Cell c={c} />}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <div className="flex flex-wrap gap-4 text-xs text-faint">
        <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-ok" /> Full</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-warnFill" /> Scoped to their part</span>
        <span className="flex items-center gap-1.5"><Minus className="h-3.5 w-3.5 text-faint" /> Not permitted</span>
      </div>

      <section className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <ControlCard icon={GitBranch} title="Delegated authority" body="Each level sets the threshold under which the level below may spend directly. Above it, spend escalates up the chain.">
          <Link to="/workspace/uganda-fire-stations/finance" className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline">Spending authority <ArrowRight className="h-3 w-3" /></Link>
        </ControlCard>
        <ControlCard icon={Users} title="Segregation of duties" body="The person who raises a spend ask cannot also approve it. Approval always rests with a different authoriser." />
        <ControlCard icon={ShieldCheck} title="Multi-signature" body="Milestone disbursements at or over 500M UGX require two distinct funder sign-offs before the tranche is released." />
      </section>
    </div>
  );
}

function ControlCard({ icon: Icon, title, body, children }: { icon: typeof Users; title: string; body: string; children?: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-line bg-card p-4">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brandSoft text-brand"><Icon className="h-4 w-4" /></span>
      <p className="mt-2 text-sm font-semibold text-ink">{title}</p>
      <p className="mt-1 text-xs leading-snug text-mute">{body}</p>
      {children && <div className="mt-2">{children}</div>}
    </div>
  );
}
